(function () {

    'use strict';

    if (typeof window === 'undefined') {

        module.exports = exportFn(
            require('./dataSourceFilterGroups.js'),
            require('./metricRequest.js'),
            require('../../modules/utils/timezone.js'),
            require('object-hash'),
            require('moment'),
            require('lodash')
        );

    } else {
        RivalIQ.module('metrics.new', function (metricsNew) {
            metricsNew.MetricResponse = exportFn(
                RivalIQ.metrics.new.DataSourceFilterGroups,
                RivalIQ.metrics.new.MetricRequest,
                RivalIQ.utils.timezone,
                window.object_hash,
                window.moment,
                window._
            );
        });
    }

    function exportFn(DataSourceFilterGroups, MetricRequest, timezoneUtils, objectHash, moment, _) {

        function addPartialTimePeriodMetadata(request, result) {
            const timeDimension = MetricRequest.getTimeDimension(request);
            if (timeDimension && result) {

                const timeDimensionValueCache = {};
                const timePeriod = request.filters.timePeriod;
                const timeDimensionIdx = _.indexOf(request.dimensions, timeDimension);

                const startDateInTz = timezoneUtils.getDateInTimezone(timePeriod.timezone, timePeriod.startDate);
                const endDateInTz = timezoneUtils.getDateInTimezone(timePeriod.timezone, timePeriod.endDate);

                _.each(result.data, dataRow => {
                    const cacheKey = `${timePeriod.timezone}_${dataRow[timeDimensionIdx]}`;
                    timeDimensionValueCache[cacheKey] ||= timezoneUtils.getDateInTimezone(timePeriod.timezone, dataRow[timeDimensionIdx]);
                    const timeDimensionValue = timeDimensionValueCache[cacheKey];
                    if (timeDimensionValue.startOf(timeDimension).isBefore(startDateInTz) || timeDimensionValue.endOf(timeDimension).isAfter(endDateInTz)) {
                        _.set(result, ['calculationMetadata', 'domains', timeDimension, dataRow[timeDimensionIdx], 'partialTimePeriod'], true);
                    }
                });
            }
        }

        // Create a standard return object for a metric request.
        // `data`, if present, should be an array of objects where each object has
        // - a "dimensions" key with key/value pairs for each freeDimension in metricRequest.
        // - a "data" key with the data.
        function makeResponse(metricRequest, {error, data, calculationMetadata}) {
            const response = {
                error,
                calculationMetadata
            };

            if (data) {
                response.dimensions = metricRequest.dimensions;
                response.data = _.map(data, datum => {
                    const row = _.map(response.dimensions, (freeDimension) => {
                        if (_.isUndefined(datum.dimensions[freeDimension]) || _.isNull(datum.dimensions[freeDimension])) {
                            throw new Error(`response is missing a dimension: ${freeDimension}`);
                        }

                        return datum.dimensions[freeDimension];
                    });

                    if (metricRequest.___returns) {
                        const dimensionName = metricRequest.___returns.value;
                        row.push(datum.dimensions[dimensionName]);
                    } else {
                        row.push(datum.data);
                    }

                    return row;
                });

                addPartialTimePeriodMetadata(metricRequest, response);
            }

            return response;
        }

        function makeErrorResponse(error) {

            let errorObj;
            if (_.isString(error)) {
                errorObj = {
                    name: 'Metric Error',
                    message: error
                };
            } else {
                errorObj = _.assign({
                    name: _.get(error, ['name'], error),
                    message: _.get(error, ['message'], error)
                }, error);
            }

            return makeResponse(null, {
                error: errorObj
            });
        }

        function isErrorResponse(response) {
            return Boolean(response.error);
        }

        function filterGroupPaths(filterGroupIds) {
            return _.flatMap(filterGroupIds, filterGroupId => {
                return _.map(DataSourceFilterGroups[filterGroupId], (filterConfig, filter) => {
                    return `filters.${filterGroupId}.${filter}`;
                });
            });
        }

        function makeFixedDimensionString(response, dataRow) {
            const fixedDimensionString = _.map(response.dimensions, (freeDimension, index) => {
                return `${freeDimension}=${dataRow[index]}`;
            }).join(';');

            return fixedDimensionString;
        }

        // Takes in an object of key => response (where key is any arbitrary string);
        // returns an array where each element of the array is an object mapping key to a data element from the response. Each response
        // in a given element in that returned array has the same fixed dimensions.
        // E.g., calling with {mainPeriod: {data: [company1DataA, company2DataA]}, comparePeriod: {data: [company1DataB, company2DataB]}}
        // results in [{mainPeriod: company1DataA, comparePeriod: company1DataB}, {mainPeriod: company2DataA, comparePeriod: company2DataB}]
        function groupResponsesByFixedDimensions(responsesByKey) {
            const responsesByDimensionString = {};

            _.each(responsesByKey, (response, key) => {
                _.each(response.data, dataRow => {
                    const fixedDimensionString = makeFixedDimensionString(response, dataRow);
                    if (!responsesByDimensionString[fixedDimensionString]) {
                        responsesByDimensionString[fixedDimensionString] = {};
                    }
                    responsesByDimensionString[fixedDimensionString][key] = dataRow;
                });
            });

            return _.values(responsesByDimensionString);
        }

        // Converts an array of arrays of data into an object of key => data, where key is the first element of each array and data the value. This
        // is done recursively for nested arrays. So
        // [ ['a', 'X', 3], ['a', 'Y', 4], ['b', 'X', 5], ['b', 'Y', 6] ] will become
        // { a: { X: 3, Y: 4 }, b: { X: 5, Y: 6 } }
        function arrayResponseToNestedObjects(responseData) {
            const dimensionality = _.size(_.first(responseData));
            if (dimensionality <= 1) {
                return _.get(responseData, [0, 0]);
            }

            const result = _(responseData)
                .groupBy(_.first)
                .mapValues(values => arrayResponseToNestedObjects(_.map(values, _.tail)))
                .value();

            return result;
        }

        // dimensions - an array of dimensions, in the order in which they'll appear in the array
        // fixedDimensions - an object with the fixed dimension values for this particular response
        // value - the value, which will be the last element of the returned array
        // E.g., passing in `(["companyId", "mediaType"], {companyId: 339, mediaType: "video"}, 123)` will result in
        // `[339, "video", 123]`
        function convertToArrayFormat(dimensions, fixedDimensions, value) {
            const dimensionsArray = _.map(dimensions, dimension => fixedDimensions[dimension]);
            return dimensionsArray.concat(value);
        }

        return {
            makeResponse,
            makeErrorResponse,
            isErrorResponse,
            groupResponsesByFixedDimensions,
            filterGroupPaths,
            arrayResponseToNestedObjects,
            convertToArrayFormat
        };
    }
})();
